import React, { useState, useEffect, useCallback } from 'react';
import { toast } from 'react-toastify';
import { API_URL } from '../config/api';

interface BrandingSettings {
  brandName: string;
  scriptGuideUrl: string;
  discountPercent: number;
  websiteUrl: string;
  supportEmail: string;
}

interface EmailTemplate {
  type: string;
  name: string;
  description: string;
}

interface EmailSettingsModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const EmailSettingsModal: React.FC<EmailSettingsModalProps> = ({ isOpen, onClose }) => {
  const [branding, setBranding] = useState<BrandingSettings>({
    brandName: 'Ssemble',
    scriptGuideUrl: '',
    discountPercent: 10,
    websiteUrl: '',
    supportEmail: ''
  });
  const [templates, setTemplates] = useState<EmailTemplate[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [previewHtml, setPreviewHtml] = useState<string>('');
  const [previewSubject, setPreviewSubject] = useState<string>('');
  const [previewLoading, setPreviewLoading] = useState(false);
  const [activePreview, setActivePreview] = useState<string | null>(null);

  const fetchBranding = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/collaborations/branding`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      if (response.ok) {
        const data = await response.json();
        setBranding(data.branding);
      }
    } catch (error) {
      console.error('Error fetching branding:', error);
    }
  }, []);

  const fetchTemplates = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/collaborations/email-templates`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      if (response.ok) {
        const data = await response.json();
        setTemplates(data.templates);
      }
    } catch (error) {
      console.error('Error fetching templates:', error);
    }
  }, []);

  useEffect(() => {
    if (isOpen) {
      setLoading(true);
      Promise.all([fetchBranding(), fetchTemplates()]).finally(() => {
        setLoading(false);
      });
    }
  }, [isOpen, fetchBranding, fetchTemplates]);

  const handleSave = async () => {
    setSaving(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/collaborations/branding`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(branding)
      });

      if (response.ok) {
        toast.success('Branding settings saved successfully');
        // Refresh preview if one is active
        if (activePreview) {
          handlePreview(activePreview);
        }
      } else {
        toast.error('Failed to save branding settings');
      }
    } catch (error) {
      console.error('Error saving branding:', error);
      toast.error('Failed to save branding settings');
    } finally {
      setSaving(false);
    }
  };

  const handlePreview = async (templateType: string) => {
    setPreviewLoading(true);
    setActivePreview(templateType);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/collaborations/email-preview/${templateType}`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      if (response.ok) {
        const data = await response.json();
        setPreviewHtml(data.html);
        setPreviewSubject(data.subject);
      } else {
        toast.error('Failed to load email preview');
      }
    } catch (error) {
      console.error('Error loading preview:', error);
      toast.error('Failed to load email preview');
    } finally {
      setPreviewLoading(false);
    }
  };

  const closePreview = () => {
    setActivePreview(null);
    setPreviewHtml('');
    setPreviewSubject('');
  };

  if (!isOpen) return null;

  return (
    <div className="modal-overlay" style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(0, 0, 0, 0.5)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 1000
    }}>
      <div style={{
        backgroundColor: 'var(--bg-primary, #ffffff)',
        borderRadius: '12px',
        width: activePreview ? '95%' : '600px',
        maxWidth: activePreview ? '1400px' : '600px',
        maxHeight: '90vh',
        overflow: 'hidden',
        display: 'flex',
        flexDirection: activePreview ? 'row' : 'column',
        boxShadow: '0 20px 40px rgba(0, 0, 0, 0.15)'
      }}>
        {/* Settings Panel */}
        <div style={{
          width: activePreview ? '400px' : '100%',
          display: 'flex',
          flexDirection: 'column',
          borderRight: activePreview ? '1px solid var(--border-color, #e5e7eb)' : 'none'
        }}>
          {/* Header */}
          <div style={{
            padding: '20px 24px',
            borderBottom: '1px solid var(--border-color, #e5e7eb)',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center'
          }}>
            <h2 style={{ margin: 0, fontSize: '18px', fontWeight: 600 }}>
              Email Settings
            </h2>
            <button
              onClick={onClose}
              style={{
                background: 'none',
                border: 'none',
                fontSize: '24px',
                cursor: 'pointer',
                color: 'var(--text-secondary, #6b7280)',
                padding: '4px'
              }}
            >
              &times;
            </button>
          </div>

          {/* Content */}
          <div style={{
            padding: '24px',
            overflowY: 'auto',
            flex: 1
          }}>
            {loading ? (
              <div style={{ textAlign: 'center', padding: '40px' }}>
                Loading...
              </div>
            ) : (
              <>
                {/* Branding Settings */}
                <div style={{ marginBottom: '32px' }}>
                  <h3 style={{
                    fontSize: '14px',
                    fontWeight: 600,
                    color: 'var(--text-secondary, #6b7280)',
                    marginBottom: '16px',
                    textTransform: 'uppercase',
                    letterSpacing: '0.05em'
                  }}>
                    Branding Settings
                  </h3>

                  <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                    <div>
                      <label style={{
                        display: 'block',
                        fontSize: '13px',
                        fontWeight: 500,
                        marginBottom: '6px',
                        color: 'var(--text-primary, #1f2937)'
                      }}>
                        Brand Name
                      </label>
                      <input
                        type="text"
                        value={branding.brandName}
                        onChange={(e) => setBranding({ ...branding, brandName: e.target.value })}
                        placeholder="Your brand name"
                        style={{
                          width: '100%',
                          padding: '10px 12px',
                          border: '1px solid var(--border-color, #e5e7eb)',
                          borderRadius: '8px',
                          fontSize: '14px',
                          backgroundColor: 'var(--bg-secondary, #f9fafb)'
                        }}
                      />
                    </div>

                    <div>
                      <label style={{
                        display: 'block',
                        fontSize: '13px',
                        fontWeight: 500,
                        marginBottom: '6px',
                        color: 'var(--text-primary, #1f2937)'
                      }}>
                        Script Guide URL
                      </label>
                      <input
                        type="url"
                        value={branding.scriptGuideUrl}
                        onChange={(e) => setBranding({ ...branding, scriptGuideUrl: e.target.value })}
                        placeholder="https://..."
                        style={{
                          width: '100%',
                          padding: '10px 12px',
                          border: '1px solid var(--border-color, #e5e7eb)',
                          borderRadius: '8px',
                          fontSize: '14px',
                          backgroundColor: 'var(--bg-secondary, #f9fafb)'
                        }}
                      />
                    </div>

                    <div>
                      <label style={{
                        display: 'block',
                        fontSize: '13px',
                        fontWeight: 500,
                        marginBottom: '6px',
                        color: 'var(--text-primary, #1f2937)'
                      }}>
                        Discount Percent
                      </label>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <input
                          type="number"
                          value={branding.discountPercent}
                          onChange={(e) => setBranding({ ...branding, discountPercent: parseInt(e.target.value) || 0 })}
                          min="0"
                          max="100"
                          style={{
                            width: '100px',
                            padding: '10px 12px',
                            border: '1px solid var(--border-color, #e5e7eb)',
                            borderRadius: '8px',
                            fontSize: '14px',
                            backgroundColor: 'var(--bg-secondary, #f9fafb)'
                          }}
                        />
                        <span style={{ color: 'var(--text-secondary, #6b7280)' }}>%</span>
                      </div>
                    </div>

                    <div>
                      <label style={{
                        display: 'block',
                        fontSize: '13px',
                        fontWeight: 500,
                        marginBottom: '6px',
                        color: 'var(--text-primary, #1f2937)'
                      }}>
                        Website URL
                      </label>
                      <input
                        type="url"
                        value={branding.websiteUrl}
                        onChange={(e) => setBranding({ ...branding, websiteUrl: e.target.value })}
                        placeholder="https://..."
                        style={{
                          width: '100%',
                          padding: '10px 12px',
                          border: '1px solid var(--border-color, #e5e7eb)',
                          borderRadius: '8px',
                          fontSize: '14px',
                          backgroundColor: 'var(--bg-secondary, #f9fafb)'
                        }}
                      />
                    </div>

                    <div>
                      <label style={{
                        display: 'block',
                        fontSize: '13px',
                        fontWeight: 500,
                        marginBottom: '6px',
                        color: 'var(--text-primary, #1f2937)'
                      }}>
                        Support Email
                        <span style={{ fontWeight: 400, color: 'var(--text-tertiary, #9ca3af)', marginLeft: '8px' }}>
                          (optional, uses sender email if empty)
                        </span>
                      </label>
                      <input
                        type="email"
                        value={branding.supportEmail}
                        onChange={(e) => setBranding({ ...branding, supportEmail: e.target.value })}
                        placeholder="support@..."
                        style={{
                          width: '100%',
                          padding: '10px 12px',
                          border: '1px solid var(--border-color, #e5e7eb)',
                          borderRadius: '8px',
                          fontSize: '14px',
                          backgroundColor: 'var(--bg-secondary, #f9fafb)'
                        }}
                      />
                    </div>
                  </div>

                  <button
                    onClick={handleSave}
                    disabled={saving}
                    style={{
                      marginTop: '20px',
                      width: '100%',
                      padding: '12px',
                      backgroundColor: saving ? '#9ca3af' : '#3b82f6',
                      color: 'white',
                      border: 'none',
                      borderRadius: '8px',
                      fontSize: '14px',
                      fontWeight: 500,
                      cursor: saving ? 'not-allowed' : 'pointer',
                      transition: 'background-color 0.2s'
                    }}
                  >
                    {saving ? 'Saving...' : 'Save Changes'}
                  </button>
                </div>

                {/* Email Templates */}
                <div>
                  <h3 style={{
                    fontSize: '14px',
                    fontWeight: 600,
                    color: 'var(--text-secondary, #6b7280)',
                    marginBottom: '16px',
                    textTransform: 'uppercase',
                    letterSpacing: '0.05em'
                  }}>
                    Email Templates
                  </h3>

                  <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                    {templates.map((template) => (
                      <div
                        key={template.type}
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'space-between',
                          padding: '12px 16px',
                          backgroundColor: activePreview === template.type
                            ? 'rgba(59, 130, 246, 0.1)'
                            : 'var(--bg-secondary, #f9fafb)',
                          borderRadius: '8px',
                          border: activePreview === template.type
                            ? '1px solid #3b82f6'
                            : '1px solid transparent'
                        }}
                      >
                        <div>
                          <div style={{
                            fontSize: '14px',
                            fontWeight: 500,
                            color: 'var(--text-primary, #1f2937)'
                          }}>
                            {template.name}
                          </div>
                          <div style={{
                            fontSize: '12px',
                            color: 'var(--text-tertiary, #9ca3af)',
                            marginTop: '2px'
                          }}>
                            {template.description}
                          </div>
                        </div>
                        <button
                          onClick={() => handlePreview(template.type)}
                          disabled={previewLoading && activePreview === template.type}
                          style={{
                            padding: '6px 12px',
                            backgroundColor: activePreview === template.type ? '#3b82f6' : '#e5e7eb',
                            color: activePreview === template.type ? 'white' : '#374151',
                            border: 'none',
                            borderRadius: '6px',
                            fontSize: '12px',
                            fontWeight: 500,
                            cursor: 'pointer',
                            transition: 'all 0.2s'
                          }}
                        >
                          {previewLoading && activePreview === template.type ? '...' : 'Preview'}
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              </>
            )}
          </div>
        </div>

        {/* Preview Panel */}
        {activePreview && (
          <div style={{
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
            backgroundColor: 'var(--bg-secondary, #f9fafb)'
          }}>
            <div style={{
              padding: '16px 24px',
              borderBottom: '1px solid var(--border-color, #e5e7eb)',
              backgroundColor: 'var(--bg-primary, #ffffff)',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center'
            }}>
              <div>
                <div style={{
                  fontSize: '12px',
                  color: 'var(--text-tertiary, #9ca3af)',
                  marginBottom: '4px'
                }}>
                  Subject:
                </div>
                <div style={{
                  fontSize: '14px',
                  fontWeight: 500,
                  color: 'var(--text-primary, #1f2937)'
                }}>
                  {previewSubject}
                </div>
              </div>
              <button
                onClick={closePreview}
                style={{
                  padding: '6px 12px',
                  backgroundColor: '#e5e7eb',
                  color: '#374151',
                  border: 'none',
                  borderRadius: '6px',
                  fontSize: '12px',
                  cursor: 'pointer'
                }}
              >
                Close Preview
              </button>
            </div>
            <div style={{
              flex: 1,
              padding: '24px',
              overflowY: 'auto'
            }}>
              {previewLoading ? (
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  height: '100%',
                  color: 'var(--text-secondary, #6b7280)'
                }}>
                  Loading preview...
                </div>
              ) : (
                <div style={{
                  backgroundColor: 'white',
                  borderRadius: '8px',
                  boxShadow: '0 2px 8px rgba(0, 0, 0, 0.08)',
                  overflow: 'hidden'
                }}>
                  <iframe
                    srcDoc={previewHtml}
                    style={{
                      width: '100%',
                      height: '600px',
                      border: 'none'
                    }}
                    title="Email Preview"
                  />
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default EmailSettingsModal;
